"use client";
import { G as CheckboxRoot, W as CheckboxIndicator } from "../chunks/vendor-base-ui-n0ony04mhklkmnfd.js";
import { n as useObservabilitySafe, t as useImpressionRef } from "../chunks/use-impression-ref-k081rlv0dt8gci3d.js";
import { n as useAccessibleNameConflictWarning, r as useAccessibleNameWarning, t as hasAccessibleName } from "../chunks/accessible-name-h2kn8kxv0zjyg24j.js";
import { t as cn } from "../chunks/cn-dk9nmc4mhy1765ud.js";
import { forwardRef, useCallback, useId } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import IconCheck from "@tabler/icons-react/dist/esm/icons/IconCheck.mjs";
import IconMinus from "@tabler/icons-react/dist/esm/icons/IconMinus.mjs";
//#region src/components/checkbox/checkbox.tsx
/**
* Checkbox variant definitions mapping variant names to their Tailwind classes.
* The component consumes these directly (see the control's `cn(...)` below), so
* the registry documents the classes that actually render.
*/
var ATOMS_CHECKBOX_VARIANTS = {
	variant: {
		default: {
			classes: "ring-od-border-primary data-checked:bg-od-bg-inverse-primary data-checked:ring-od-border-focused-primary data-indeterminate:bg-od-bg-inverse-primary data-indeterminate:ring-od-border-focused-primary",
			description: "Default checkbox appearance"
		},
		error: {
			classes: "ring-od-border-error data-checked:bg-od-bg-error data-checked:ring-od-border-error data-indeterminate:bg-od-bg-error data-indeterminate:ring-od-border-error",
			description: "Error state for validation failures"
		}
	},
	size: {
		xs: {
			classes: "h-5 w-5 rounded-md",
			description: "20px box"
		},
		sm: {
			classes: "h-6 w-6 rounded-md",
			description: "24px box"
		},
		md: {
			classes: "h-7 w-7 rounded-md",
			description: "28px box (default)"
		}
	}
};
var ATOMS_CHECKBOX_DEFAULT_VARIANTS = {
	variant: "default",
	size: "md"
};
function checkboxVariants({ variant = ATOMS_CHECKBOX_DEFAULT_VARIANTS.variant, size = ATOMS_CHECKBOX_DEFAULT_VARIANTS.size } = {}) {
	return cn(ATOMS_CHECKBOX_VARIANTS.variant[variant].classes, ATOMS_CHECKBOX_VARIANTS.size[size].classes);
}
var ICON_SIZES = {
	xs: 14,
	sm: 16,
	md: 18
};
/**
* Boolean (or tri-state) selection control. Pass `caption` to render label text
* to the right of the box (clicking it toggles, and it names the control for
* assistive tech); otherwise give it an accessible name via `aria-label` /
* `aria-labelledby`. Use the `error` variant to surface validation failures and
* `indeterminate` for "some but not all" parent/child selections.
*
* State is controlled via `checked` + `onCheckedChange`; `name`/`value` wire it
* up for native form submission.
*
* @example
* ```tsx
* <Checkbox checked={checked} onCheckedChange={setChecked} caption="Accept terms and conditions" />
*
* <Checkbox variant="error" size="sm" aria-label="Required option" />
* <Checkbox indeterminate aria-label="Select all" />
* ```
*/
var Checkbox = forwardRef(({ className, checked, indeterminate, disabled, variant = "default", size = "md", onCheckedChange, name, value, caption, id, analyticsName, trackImpression, ...props }, ref) => {
	const iconSize = ICON_SIZES[size];
	const { trackJourneyEvent } = useObservabilitySafe();
	const viewRef = useImpressionRef(Boolean(analyticsName && trackImpression), analyticsName, "checkboxViewEvent", ref);
	useAccessibleNameWarning("Checkbox", hasAccessibleName({
		...props,
		id
	}, caption != null));
	useAccessibleNameConflictWarning("Checkbox", caption != null && Boolean(props["aria-label"] || props["aria-labelledby"]));
	const renderIndicator = useCallback((renderProps, state) => /* @__PURE__ */ jsx("span", {
		...renderProps,
		children: state.indeterminate ? /* @__PURE__ */ jsx(IconMinus, {
			stroke: 2.5,
			size: iconSize
		}) : /* @__PURE__ */ jsx(IconCheck, {
			stroke: 2.5,
			size: iconSize
		})
	}), [iconSize]);
	const handleCheckedChange = useCallback((nextChecked, eventDetails) => {
		if (analyticsName) trackJourneyEvent("platform", "checkboxClickEvent", {
			targetName: analyticsName,
			optionName: value
		});
		onCheckedChange?.(nextChecked, eventDetails);
	}, [
		analyticsName,
		onCheckedChange,
		value,
		trackJourneyEvent
	]);
	const generatedId = useId();
	const controlId = id ?? (caption != null ? generatedId : void 0);
	const box = /* @__PURE__ */ jsx(CheckboxRoot, {
		ref: viewRef,
		id: controlId,
		name,
		value,
		checked,
		indeterminate,
		disabled,
		onCheckedChange: handleCheckedChange,
		className: cn("relative flex items-center justify-center border-0 ring-1 focus:outline-none after:absolute after:-inset-x-3 after:-inset-y-2", ATOMS_CHECKBOX_VARIANTS.size[size].classes, disabled ? "bg-od-bg-hover-primary ring-od-border-primary cursor-not-allowed" : ["bg-od-bg-primary cursor-pointer hover:ring-od-border-hover-primary focus-visible:ring-od-border-focused-accent-brand", ATOMS_CHECKBOX_VARIANTS.variant[variant].classes], className),
		...props,
		children: /* @__PURE__ */ jsx(CheckboxIndicator, {
			keepMounted: true,
			className: cn("flex items-center justify-center data-unchecked:invisible", disabled ? "text-od-content-tertiary" : "text-od-content-inverse-primary"),
			render: renderIndicator
		})
	});
	if (caption == null) return box;
	return /* @__PURE__ */ jsxs("span", {
		className: "inline-flex items-center gap-3",
		children: [box, /* @__PURE__ */ jsx("label", {
			htmlFor: controlId,
			className: cn("text-body-md text-od-content-primary", disabled ? "cursor-not-allowed" : "cursor-pointer"),
			children: caption
		})]
	});
});
Checkbox.displayName = "Checkbox";
//#endregion
export { ATOMS_CHECKBOX_DEFAULT_VARIANTS, ATOMS_CHECKBOX_VARIANTS, Checkbox, checkboxVariants };

